import { DateTime } from 'luxon';
import { z } from 'zod';

import { safelyConvertDate } from './conversion';

export interface DateTimeSchemaParams {
  readonly allowEmptyString?: boolean;
  /**
   * Defines the date formatting options, {@link Intl.DateTimeFormatOptions}, that should be used
   * when displaying the minimum or maximum dates in error messages.
   *
   * @default {DateTime.DateShort}
   */
  readonly errorMessageFormat?: Intl.DateTimeFormatOptions;
  /**
   * Defines the message that should be included in the {@link z.ZodError} when the date is invalid.
   *
   * @default "Invalid date"
   */
  readonly invalidMessage?: string;
  readonly isNullable?: boolean;
  /**
   * An optional maximum date, {@link DateTime}, that the date must be before.
   */
  readonly max?: Date | DateTime;
  /**
   * Defines the message that should be included in the {@link z.ZodError} when the date exceeds
   * the maximum.
   *
   * @default "Date must be before {max}."
   */
  readonly maxMessage?: string;
  /**
   * An optional minimum date, {@link DateTime}, that the date must be after.
   */
  readonly min?: Date | DateTime;
  /**
   * Defines the message that should be included in the {@link z.ZodError} when the date exceeds
   * the minimum.
   *
   * @default "Date must be after {min}."
   */
  readonly minMessage?: string;
}

export type DateTimeSchemaTransformerReturn<O extends DateTimeSchemaParams> = O extends {
  readonly allowEmptyString: true;
}
  ? DateTime<true> | null
  : O extends { readonly isNullable: true }
    ? DateTime<true> | null
    : DateTime<true>;

export type DateTimeSchemaType<
  Base extends z.ZodType,
  O extends DateTimeSchemaParams,
> = z.ZodEffects<Base, DateTimeSchemaTransformerReturn<O>>;

export type DateTimeSchemaTransformer<O extends DateTimeSchemaParams> = (
  value: unknown,
  ctx: z.RefinementCtx,
) => DateTimeSchemaTransformerReturn<O>;

export const DateTimeSchemaTransformer = <O extends DateTimeSchemaParams>({
  allowEmptyString = false,
  errorMessageFormat = DateTime.DATE_SHORT,
  invalidMessage = 'Invalid date',
  isNullable = false,
  max,
  min,
  ...params
}: O): DateTimeSchemaTransformer<O> => {
  const minDate = DateTime.isDateTime(min) ? min : min ? DateTime.fromJSDate(min) : undefined;
  const maxDate = DateTime.isDateTime(max) ? max : max ? DateTime.fromJSDate(max) : undefined;

  if (minDate && maxDate && maxDate <= minDate) {
    throw new Error('The minimum date must be before the maximum date.');
  } else if (maxDate && !maxDate.isValid) {
    throw new Error('The maximum date is invalid!');
  } else if (minDate && !minDate.isValid) {
    throw new Error('The minimum date is invalid!');
  }

  const exceedsMax = (
    dt: DateTime<true>,
    _maxDt: DateTime<true> | undefined,
  ): _maxDt is DateTime<true> => _maxDt !== undefined && dt >= _maxDt;

  const exceedsMin = (
    dt: DateTime<true>,
    _minDt: DateTime<true> | undefined,
  ): _minDt is DateTime<true> => _minDt !== undefined && dt <= _minDt;

  return (value: unknown, ctx: z.RefinementCtx): DateTimeSchemaTransformerReturn<O> => {
    const sanitized = typeof value === 'string' ? value.trim() : value;
    if (typeof sanitized === 'string' && sanitized.trim().length === 0 && allowEmptyString) {
      return null as DateTimeSchemaTransformerReturn<O>;
    }

    if (value === null && isNullable) {
      return null as DateTimeSchemaTransformerReturn<O>;
    }

    const datetime = safelyConvertDate(sanitized, { returnAs: 'DateTime', strict: false });
    if (!datetime) {
      ctx.addIssue({ code: z.ZodIssueCode.invalid_date, message: invalidMessage });
      return z.NEVER;
    }
    if (exceedsMax(datetime, maxDate) || exceedsMin(datetime, minDate)) {
      if (exceedsMax(datetime, maxDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          inclusive: false,
          maximum: maxDate.toUnixInteger(),
          message:
            params.maxMessage ??
            `Date must be before ${maxDate.toLocaleString(errorMessageFormat)}.`,
          type: 'date',
        });
      }
      if (exceedsMin(datetime, minDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_date,
          message:
            params.minMessage ??
            `Date must be after ${minDate.toLocaleString(errorMessageFormat)}.`,
        });
      }
      return z.NEVER;
    }
    return datetime as DateTimeSchemaTransformerReturn<O>;
  };
};

export const DateTimeSchema = z.custom<DateTime>((data: unknown) => DateTime.isDateTime(data));

export const ValidDateTimeSchema = <
  O extends DateTimeSchemaParams,
  Base extends z.ZodType = z.ZodType<DateTime<boolean>, z.ZodTypeDef, DateTime<boolean>>,
>(
  params: O,
  base: Base = DateTimeSchema as Base,
): DateTimeSchemaType<Base, O> => base.transform(DateTimeSchemaTransformer(params));

export const SafeDateTimeSchema = <O extends DateTimeSchemaParams>(
  params: O,
): DateTimeSchemaType<z.ZodUnknown, O> => z.unknown().transform(DateTimeSchemaTransformer(params));
